import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, ArrowUpRight, Box } from "lucide-react";
import { useModels } from "../context/ModelsContext";
import XeokitViewer from "./XeokitViewer";
import ImageCarousel from "./ImageCarousel";
import "../styles/design-system.css";
import "../styles/homepage-clean.css";
import DecorativeDiamond, {
  DecorativeSectionDivider,
  CornerOrnament,
  FloatingDecorations,
} from "./DecorativeDiamond";

// Analytics tracking functions - replace with your actual analytics implementation
const trackSocialClick = (platform) => {
  try {
    // Replace with your analytics implementation (Google Analytics, Mixpanel, etc.)
    // Example: gtag('event', 'social_click', { platform });
  } catch (error) {
    console.error("Analytics tracking failed:", error);
  }
};

const trackFooterClick = (section, link) => {
  try {
    // Example: gtag('event', 'footer_click', { section, link });
  } catch (error) {
    console.error("Analytics tracking failed:", error);
  }
};

function Homepage() {
  const navigate = useNavigate();
  const { models, loading } = useModels();
  const [currentSlide, setCurrentSlide] = useState(0);
  const autoPlayInterval = 6000;

  const slides = [
    {
      id: "welcome",
      badge: "Moroccan Design BIM",
      title: "Bienvenue sur MDBIM",
      description:
        "Découvrez notre plateforme de visualisation 3D pour modèles BIM. Explorez des fichiers IFC avec une technologie de pointe.",
      cta: "Explorer la Galerie",
      image: "/heroslides/Slide1.png",
      alt: "Interface de visualisation 3D MDBIM avec modèles BIM",
    },
    {
      id: "models",
      badge: "Moroccan Design BIM",
      title: "Bibliothèque BIM Complète",
      description:
        "Accédez à une vaste collection de modèles IFC organisés par catégorie. Zelige, Boiserie, Platre et Ferronnerie.",
      cta: "Voir les Modèles",
      image: "/heroslides/Slide2.png",
      alt: "Collection de modèles BIM organisés par catégorie",
    },
    {
      id: "viewer",
      badge: "Moroccan Design BIM",
      title: "Viewer 3D Intégré",
      description:
        "Visualisez vos modèles BIM directement dans le navigateur avec notre viewer 3D haute performance basé sur xeokit.",
      cta: "Tester le Viewer",
      image: "/heroslides/Slide3.png",
      alt: "Viewer 3D haute performance pour modèles BIM",
    },
  ];

  const categories = [
    {
      name: "Zelige",
      description: "Carreaux de mosaïque traditionnels marocains",
      features: [
        "Carreaux émaillés",
        "Motifs géométriques",
        "Couleurs personnalisées",
      ],
    },
    {
      name: "Boiserie",
      description: "Panneaux et ornements en bois sculpté",
      features: ["Bois de cèdre", "Sculptures à la main", "Détails complexes"],
    },
    {
      name: "Platre",
      description: "Décorations et moulures en plâtre",
      features: [
        "Détails sculptés",
        "Motifs traditionnels",
        "Designs personnalisés",
      ],
    },
    {
      name: "Ferronnerie",
      description: "Éléments en fer forgé et métalliques",
      features: [
        "Fer forgé artisanal",
        "Designs traditionnels",
        "Finitions personnalisées",
      ],
    },
  ];

  // Get featured models from R2 storage
  const featuredModels = models.filter((model) => model.featured).slice(0, 4);

  // If no featured models, get first 4 models
  const availableModels =
    featuredModels.length > 0 ? featuredModels : models.slice(0, 4);

  // Map to products format
  const products =
    availableModels.length > 0
      ? availableModels.map((model) => ({
          id: model.id,
          title: model.name,
          category: model.category,
          description: model.description,
          mockupImages: model.mockupImages || [],
          xktFile: model.xktFile || "",
        }))
      : [
          {
            id: 1,
            title: "Modèle BIM Exemple",
            category: "Ferronnerie",
            description:
              "Modèle d'exemple en cours de chargement depuis le stockage cloud.",
            mockupImages: [],
            xktFile: "",
          },
        ];

  const features = [];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, autoPlayInterval);

    return () => clearInterval(interval);
  }, [slides.length, autoPlayInterval]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  return (
    <div className="homepage">
      {/* Hero Carousel */}
      <section className="hero-carousel">
        {/* Floating Decorative Elements in Hero */}
        <div className="absolute inset-0 pointer-events-none z-10">
          <div className="absolute top-20 left-10 opacity-20">
            <DecorativeDiamond size="sm" variant="outline" animated />
          </div>
          <div className="absolute top-32 right-20 opacity-15">
            <DecorativeDiamond size="xs" variant="gold" />
          </div>
          <div className="absolute bottom-40 left-16 opacity-25">
            <DecorativeDiamond size="md" variant="subtle" animated />
          </div>
          <div className="absolute top-1/3 right-10 opacity-10">
            <DecorativeDiamond size="lg" variant="gradient" />
          </div>
        </div>

        {/* Background Image with Overlay */}
        <div className="hero-background">
          <img
            src={slides[currentSlide].image}
            alt={slides[currentSlide].alt}
            className="hero-image"
            loading="eager"
            onError={(e) => {
              console.error("Hero image failed to load:", e.target.src);
              e.target.style.display = "none";
            }}
          />
          <div className="hero-overlay"></div>
        </div>

        {/* Moroccan Pattern Overlay */}
        <div className="hero-pattern moroccan-pattern"></div>

        {/* Corner Ornaments - Hidden on Mobile */}
        <div className="corner-ornaments">
          <div className="corner-ornament corner-tl"></div>
          <div className="corner-ornament corner-tr"></div>
          <div className="corner-ornament corner-bl"></div>
          <div className="corner-ornament corner-br"></div>
        </div>

        {/* Decorative Top Divider - Hidden on Mobile */}
        <div className="decorative-divider decorative-divider-top">
          <div className="divider-line"></div>
          <div className="divider-diamond"></div>
          <div className="divider-line"></div>
        </div>

        {/* Carousel Content */}
        <div className="carousel-content">
          <div key={currentSlide} className="slide-content animate-slide-up">
            <div className="slide-badge">
              <div className="badge-diamond"></div>
              <span className="text-xs tracking-widest font-body font-light">
                {slides[currentSlide].badge}
              </span>
              <div className="badge-diamond"></div>
            </div>

            <h1 className="slide-title font-display font-bold text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-7xl text-white mb-4 sm:mb-6">
              {slides[currentSlide].title}
            </h1>

            <p className="slide-description text-sm sm:text-base lg:text-lg text-gray-200 mb-6 sm:mb-8 max-w-xl lg:max-w-2xl mx-auto px-4 sm:px-0">
              {slides[currentSlide].description}
            </p>

            <div className="slide-buttons flex flex-col sm:flex-row gap-4 sm:gap-6 px-4 sm:px-0">
              <button
                className="btn btn-primary w-full sm:w-auto"
                onClick={() => navigate("/gallery")}
                aria-label={`${slides[currentSlide].cta} - Naviguer vers la galerie`}
              >
                {slides[currentSlide].cta}
                <ArrowUpRight className="w-4 h-4" />
              </button>
              <button
                className="btn btn-outline w-full sm:w-auto"
                onClick={() => navigate("/about")}
                aria-label="En savoir plus sur MDBIM"
              >
                En Savoir Plus
                <ArrowUpRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="carousel-nav">
          <button
            className="nav-btn nav-prev"
            onClick={prevSlide}
            aria-label="Diapositive précédente"
          >
            <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>

          <div className="nav-indicators">
            {slides.map((_, index) => (
              <button
                key={index}
                className={`nav-indicator ${
                  index === currentSlide ? "active" : ""
                }`}
                onClick={() => goToSlide(index)}
                aria-label={`Aller à la diapositive ${index + 1}`}
              />
            ))}
          </div>

          <button
            className="nav-btn nav-next"
            onClick={nextSlide}
            aria-label="Diapositive suivante"
          >
            <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
        </div>

        {/* Decorative Bottom Divider - Hidden on Mobile */}
        <div className="decorative-divider decorative-divider-bottom">
          <div className="divider-line"></div>
          <div className="divider-grid">
            {[...Array(9)].map((_, i) => (
              <div
                key={i}
                className={`grid-square ${i === 4 ? "active" : ""}`}
              ></div>
            ))}
          </div>
          <div className="divider-line"></div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="categories-section py-24 bg-card moroccan-pattern">
        <div className="container">
          {/* Section Header */}
          <div className="section-header text-center mb-16 relative">
            {/* Decorative elements around section header */}
            <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 opacity-30">
              <DecorativeDiamond size="xs" variant="filled" />
            </div>
            <div className="absolute top-4 -left-8 opacity-20 hidden lg:block">
              <DecorativeDiamond size="sm" variant="outline" animated />
            </div>
            <div className="absolute top-4 -right-8 opacity-20 hidden lg:block">
              <DecorativeDiamond size="sm" variant="outline" animated />
            </div>

            <div className="decorative-label">
              <div className="label-line"></div>
              <DecorativeDiamond size="xs" variant="gold" />
              <span className="text-xs tracking-widest font-body font-medium text-muted-foreground">
                NOS CATÉGORIES
              </span>
              <DecorativeDiamond size="xs" variant="gold" />
              <div className="label-line"></div>
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-foreground mb-4">
              Modèles BIM par Catégorie
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Explorez notre bibliothèque complète de modèles BIM organisés par
              domaine d'expertise. Chaque catégorie contient des modèles IFC de
              haute qualité prêts à l'emploi.
            </p>
          </div>

          {/* Categories Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {categories.map((category, index) => (
              <CategoryCard
                key={category.name}
                category={category}
                index={index}
              />
            ))}
          </div>

          {/* Decorative Footer */}
          <div className="decorative-footer mt-16 relative">
            {/* Additional artistic diamonds around the footer diamonds */}
            <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 opacity-20">
              <DecorativeDiamond size="sm" variant="gold" animated />
            </div>
            <div className="absolute -bottom-4 left-1/3 opacity-15">
              <DecorativeDiamond size="xs" variant="outline" />
            </div>
            <div className="absolute -bottom-4 right-1/3 opacity-15">
              <DecorativeDiamond size="xs" variant="outline" />
            </div>

            <div className="footer-diamonds">
              {[...Array(5)].map((_, i) => (
                <div
                  key={i}
                  className={`diamond ${i === 2 ? "active" : ""}`}
                ></div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section className="products-section py-24">
        <div className="container">
          <div className="section-header text-center mb-16 relative">
            {/* Decorative elements for products section */}
            <div className="absolute -top-6 left-1/4 opacity-25 hidden md:block">
              <DecorativeDiamond size="sm" variant="gradient" animated />
            </div>
            <div className="absolute -top-6 right-1/4 opacity-25 hidden md:block">
              <DecorativeDiamond size="sm" variant="gradient" animated />
            </div>

            <div className="decorative-label">
              <div className="label-line"></div>
              <DecorativeDiamond size="xs" variant="terracotta" />
              <span className="text-xs tracking-widest font-body font-medium text-muted-foreground">
                MODÈLES VEDETTES
              </span>
              <DecorativeDiamond size="xs" variant="terracotta" />
              <div className="label-line"></div>
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-foreground mb-4">
              Collection Premium
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Découvrez notre sélection de modèles BIM premium avec des détails
              exceptionnels et des propriétés techniques complètes pour vos
              projets.
            </p>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 relative">
              {/* Decorative elements during loading */}
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 opacity-10 pointer-events-none">
                <DecorativeDiamond size="3xl" variant="gradient" animated />
              </div>

              {[...Array(4)].map((_, index) => (
                <div
                  key={index}
                  className="product-card animate-slide-up"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="card-image bg-muted flex items-center justify-center relative">
                    <div className="loading-spinner"></div>
                    {/* Small decorative element in loading cards */}
                    <div className="absolute top-2 right-2 opacity-20">
                      <DecorativeDiamond size="xs" variant="outline" />
                    </div>
                  </div>
                  <div className="card-content p-5">
                    <div className="h-6 bg-muted rounded mb-2"></div>
                    <div className="h-4 bg-muted rounded mb-4"></div>
                    <div className="h-10 bg-muted rounded"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {products.map((product, index) => (
                <ProductCard key={product.id} product={product} index={index} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* About Section */}
      <section className="about-section py-24 bg-card">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left Column */}
            <div className="relative">
              {/* Decorative elements in about section */}
              <div className="absolute -top-4 -left-4 opacity-20">
                <DecorativeDiamond size="md" variant="subtle" />
              </div>
              <div className="absolute top-20 -right-6 opacity-15">
                <DecorativeDiamond size="sm" variant="gold" animated />
              </div>

              <div className="decorative-label mb-6">
                <DecorativeDiamond size="xs" variant="outline" />
                <span className="text-xs tracking-widest font-body font-medium text-muted-foreground">
                  À PROPOS
                </span>
                <DecorativeDiamond size="xs" variant="outline" />
              </div>
              <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mb-6">
                L'Avenir de la Visualisation BIM
              </h2>
              <div className="space-y-4 text-muted-foreground mb-8">
                <p>
                  MDBIM (Moroccan Design BIM) est une plateforme innovante
                  dédiée à la visualisation et au partage de modèles BIM. Nous
                  utilisons les dernières technologies WebGL pour offrir une
                  expérience 3D fluide directement dans votre navigateur.
                </p>
                
                <p>
                  Notre mission est de démocratiser l'accès aux modèles IFC et
                  de faciliter la collaboration entre tous les acteurs du
                  secteur de la construction et de l'architecture.
                </p>
                <br />
                <p>
                  La décoration marocaine est une culture riche et ancrée dans le patrimoine marocain, depuis des siècles, elle a pu laisser une empreinte intemporelle de notre culturel qui a dépassé les frontières marocaines.
Notre plateforme Bim Moroccan Design a pour but de conserver ce patrimoine et de le moderniser afin de le promouvoir et de l’adapter à une industrialisation du secteur du bâtiment, ainsi que de l’intégrer au Bim. Notre vision consiste à dédiée cette plateforme à la décoration marocaine, alimentée d’objet de décoration par thématique faux plafond, revêtement sol et menuiserie bois, accessible à tous les acteurs du monde du Bim.
                </p>
              </div>

              {/* Contact Info */}
              <div className="contact-info space-y-4 pt-8 border-t border-border">
                <div>
                  <h3 className="font-semibold text-foreground mb-2">
                    Contactez-nous
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    contact@moroccandesignbim.com
                  </p>
                </div>
              </div>
            </div>

            {/* Right Column - Features */}
            <div className="grid grid-cols-2 gap-4 relative">
              {/* Decorative elements around features */}
              <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 opacity-20">
                <DecorativeDiamond size="lg" variant="gradient" animated />
              </div>
              <div className="absolute bottom-4 -right-8 opacity-15">
                <DecorativeDiamond size="md" variant="gold" />
              </div>

              {features.length > 0 ? (
                features.map((feature, index) => (
                  <FeatureCard
                    key={feature.title}
                    feature={feature}
                    index={index}
                  />  
                ))
              ) : (
                // Placeholder when no features
                <div className="col-span-2 flex items-center justify-center p-12">
                  <div className="text-center">
                    <DecorativeDiamond size="xl" variant="subtle" animated />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Professional Technical Footer */}
      <footer className="tech-footer">
        <div className="tech-footer-content">
          {/* Column 1: About Us */}
          <div className="tech-footer-column tech-footer-about">
            <div className="footer-logo-section">
              <img
                src="/logo/Logo.png"
                alt="Logo MDBIM"
                className="footer-logo"
              />
              <h3 className="tech-footer-title">MDBIM</h3>
            </div>
            <p className="footer-description">
              Moroccan Design BIM est une plateforme innovante pour visualiser et partager des modèles architecturaux. Nous transformons les données BIM en expériences 3D interactives, rendant la collaboration en conception transparente et accessible.
            </p>
          </div>

          {/* Column 2: Navigation */}
          <div className="tech-footer-column">
            <h3 className="tech-footer-title">Navigation</h3>
            <ul className="tech-footer-links">
              <li>
                <button
                  onClick={() => {
                    trackFooterClick("Navigation", "Accueil");
                    navigate("/");
                    window.scrollTo(0, 0);
                  }}
                  className="tech-link"
                  aria-label="Aller à l'accueil"
                >
                  Accueil
                </button>
              </li>
              <li>
                <button
                  onClick={() => {
                    trackFooterClick("Navigation", "Galerie");
                    navigate("/gallery");
                    window.scrollTo(0, 0);
                  }}
                  className="tech-link"
                  aria-label="Voir la galerie de modèles"
                >
                  Galerie
                </button>
              </li>
              <li>
                <button
                  onClick={() => {
                    trackFooterClick("Navigation", "À propos");
                    navigate("/about");
                    window.scrollTo(0, 0);
                  }}
                  className="tech-link"
                  aria-label="En savoir plus sur nous"
                >
                  À propos
                </button>
              </li>
              <li>
                <button
                  onClick={() => {
                    trackFooterClick("Navigation", "Contact");
                    navigate("/about");
                    setTimeout(() => {
                      window.scrollTo(0, document.body.scrollHeight);
                    }, 100);
                  }}
                  className="tech-link"
                  aria-label="Nous contacter"
                >
                  Contact
                </button>
              </li>
            </ul>
          </div>

          {/* Column 3: Contact Details */}
          <div className="tech-footer-column">
            <h3 className="tech-footer-title">Contact</h3>
            <ul className="tech-footer-contact">
              <li>
                <span className="contact-label">Téléphone:</span>
                <a href="tel:+212661193395" className="tech-link">
                  +212 661-193395
                </a>
              </li>
              <li>
                <span className="contact-label">Email:</span>
                <a href="mailto:contact@mdbim.ma" className="tech-link">
                  contact@mdbim.ma
                </a>
              </li>
            </ul>
          </div>

          {/* Column 4: Legal */}
          <div className="tech-footer-column">
            <h3 className="tech-footer-title">Légal</h3>
            <ul className="tech-footer-links">
              <li>
                <button
                  onClick={() => {
                    trackFooterClick("Legal", "Confidentialité");
                    navigate("/privacy");
                    window.scrollTo(0, 0);
                  }}
                  className="tech-link"
                  aria-label="Politique de confidentialité"
                >
                  Confidentialité
                </button>
              </li>
              <li>
                <button
                  onClick={() => {
                    trackFooterClick("Legal", "CGU");
                    navigate("/terms");
                    window.scrollTo(0, 0);
                  }}
                  className="tech-link"
                  aria-label="Conditions générales d'utilisation"
                >
                  CGU
                </button>
              </li>
            </ul>
          </div>
        </div>

        {/* Copyright Bar */}
        <div className="tech-footer-bottom">
          <div className="tech-footer-copyright">
            <span className="copyright-left">Conçu par Raven's Quill Development</span>
            <span className="copyright-right">&copy; 2026 Moroccan Design BIM. Tous droits réservés.</span>
          </div>
        </div>
      </footer>

      {/* Structured Data for SEO */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "MDBIM - Moroccan Design BIM",
          description:
            "Plateforme de visualisation BIM innovante pour modèles IFC et fichiers 3D",
          url: "https://mdbim.ma",
          logo: "https://mdbim.ma/logo/Logo.png",
          sameAs: [
            "https://twitter.com/mdbim_ma",
            "https://github.com/mdbim",
            "https://linkedin.com/company/mdbim",
          ],
          contactPoint: {
            "@type": "ContactPoint",
            contactType: "customer service",
            url: "https://mdbim.ma/support",
          },
        })}
      </script>
    </div>
  );
}

// Category Card Component
function CategoryCard({ category, index }) {
  const navigate = useNavigate();

  const handleClick = () => {
    // Navigate to gallery with category filter
    navigate(`/gallery/category/${category.name}`);
  };

  return (
    <div
      className="category-card animate-slide-up group"
      style={{ animationDelay: `${index * 0.15}s` }}
      onClick={handleClick}
    >
      {/* Corner Decorations */}
      <CornerOrnament position="top-left" size="sm" />
      <CornerOrnament position="top-right" size="sm" />
      <CornerOrnament position="bottom-left" size="sm" />
      <CornerOrnament position="bottom-right" size="sm" />

      {/* Lantern Icon - Idle by default, Moving on hover */}
      <div className="lantern-container">
        <img
          src="/cards lantern/idle lantern.png"
          alt="Lantern"
          className="lantern-idle"
        />
        <img
          src="/cards lantern/moving lantern.png"
          alt="Lantern animé"
          className="lantern-moving"
        />
      </div>

      {/* Content */}
      <div className="card-content">
        <div className="card-header relative">
          {/* Small decorative diamond next to title */}
          <div className="absolute -left-6 top-1 opacity-30">
            <DecorativeDiamond size="xs" variant="gold" />
          </div>

          <h3 className="text-2xl font-display font-semibold text-foreground mb-2 flex items-center gap-2">
            {category.name}
            <ArrowUpRight className="w-5 h-5 opacity-0 group-hover:opacity-100 transition-opacity" />
          </h3>
          <p className="text-sm text-muted-foreground mb-4">
            {category.description}
          </p>
        </div>

        {/* Features */}
        <div className="features flex flex-wrap gap-2 mb-4">
          {category.features.map((feature, i) => (
            <span
              key={i}
              className="feature-tag text-xs border border-border px-2 py-1 text-muted-foreground"
            >
              {feature}
            </span>
          ))}
        </div>

        {/* Bottom Line */}
        <div className="bottom-line"></div>
      </div>
    </div>
  );
}

// Product Card Component
function ProductCard({ product, index }) {
  const navigate = useNavigate();

  const handleClick = () => {
    // Navigate to product details in gallery
    navigate(`/gallery/product/${product.id}`);
  };

  return (
    <div
      className="product-card animate-slide-up group"
      style={{ animationDelay: `${index * 0.1}s` }}
      onClick={handleClick}
    >
      {/* Corner Decoration */}
      <CornerOrnament position="top-right" size="xs" />

      <div className="card-image">
        {product.mockupImages && product.mockupImages.length > 0 ? (
          <ImageCarousel
            images={product.mockupImages}
            alt={product.title}
            height="100%"
            objectFit="cover"
          />
        ) : product.xktFile ? (
          <XeokitViewer
            modelUrl={product.xktFile}
            height="100%"
            width="100%"
            enableZoom={false}
          />
        ) : (
          <div className="image-placeholder bg-muted flex items-center justify-center text-muted-foreground flex-col">
            <Box className="w-8 h-8" />
            <span className="text-xs mt-2">Chargement...</span>
          </div>
        )}
        <div className="category-badge bg-background border border-border text-xs px-2 py-1">
          {product.category}
        </div>
      </div>

      <div className="card-content p-5">
        <h3 className="font-display font-semibold text-foreground mb-2 group-hover:text-foreground transition-colors">
          {product.title}
        </h3>
        <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
          {product.description ||
            "Modèle BIM de haute qualité disponible au téléchargement."}
        </p>
        <button className="btn-ghost w-full flex items-center justify-between group-hover:text-foreground transition-colors">
          <span>Voir Détails</span>
          <ArrowUpRight className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
        </button>
      </div>
    </div>
  );
}

// Feature Card Component
function FeatureCard({ feature, index }) {
  const Icon = feature.icon;

  return (
    <div
      className="feature-card p-4 border border-border bg-background hover:border-foreground/20 transition-colors animate-slide-up"
      style={{ animationDelay: `${index * 0.1}s` }}
    >
      <Icon className="w-5 h-5 mb-3" />
      <h4 className="text-sm font-display font-semibold text-foreground mb-1">
        {feature.title}
      </h4>
      <p className="text-xs text-muted-foreground">{feature.description}</p>
    </div>
  );
}

export default Homepage;
